import { css } from "@emotion/css";
import { Key, PropsWithChildren } from "react";

import { Stack } from "../../foundations/grid-layout/Stack";
import { Icon } from "../../foundations/icons/Icon/Icon";
import { Text } from "../../foundations/typography/Text";
import { AccordionGroup } from "../accordion/AccordionGroup";
import { AccordionGroupPanel } from "../accordion/AccordionGroupPanel";
import { AccordionGroupToggle } from "../accordion/AccordionGroupToggle";

interface CollapsibleGroupProps {
  items: {
    title: string;
    render: () => React.ReactElement;
  }[];
}

function CollpasibleTrigger({
  id,
  title,
  isOpen,
}: {
  id: Key;
  title: string;
  isOpen: boolean;
}) {
  return (
    <div
      className={css(
        styles.trigger,
        isOpen && {
          gap: "var(--space-4)",
          borderBottom: "unset",
          borderRadius: "var(--border-radius-4) var(--border-radius-4) 0 0",
        }
      )}
    >
      <AccordionGroupToggle
        id={id}
        className={css({
          // overflow: hidden - does not work here as the Icon inside requires focus which spills over
          borderRadius: "var(--border-radius-2)",
        })}
      >
        <Stack orientation="horizontal">
          <Text
            style="global/heading-medium"
            color="text-weak"
            className={css({
              flex: 1,
            })}
          >
            {title}
          </Text>
          <Icon
            name={isOpen ? "close" : "unfold_more"}
            size="space-5"
            color="icon-weak"
            type="mini"
          />
        </Stack>
      </AccordionGroupToggle>
      <AccordionGroupPanel id={id}>{null}</AccordionGroupPanel>
    </div>
  );
}

function CollapsiblePanel({
  id,
  className,
  children,
}: {
  id: Key;
  className?: string;
} & PropsWithChildren) {
  return (
    <AccordionGroupPanel id={id}>
      <div className={css(styles.panel, className)}>{children}</div>
    </AccordionGroupPanel>
  );
}

export function CollapsibleGroup({ items }: CollapsibleGroupProps) {
  return (
    <AccordionGroup>
      {({ isOpen }) => (
        <Stack orientation="vertical" gap="space-0">
          <Stack orientation="horizontal" gap="space-2" align="start">
            {items.map(({ title }, i) => (
              <CollpasibleTrigger
                key={i}
                id={i}
                title={title}
                isOpen={isOpen(i)}
              />
            ))}
          </Stack>

          {items.map(({ render }, i) => (
            <CollapsiblePanel
              key={i}
              id={i}
              className={
                i === 0
                  ? css({ borderTopLeftRadius: 0 })
                  : i === items.length - 1
                    ? css({ borderTopRightRadius: 0 })
                    : undefined
              }
            >
              {render()}
            </CollapsiblePanel>
          ))}
        </Stack>
      )}
    </AccordionGroup>
  );
}

const styles = {
  trigger: css({
    display: "flex",
    flex: "1",
    flexDirection: "column",
    gap: "0px",
    transition: "gap 0.2s ease-out",
    padding: "var(--space-4)",
    border: "1px solid var(--border-base)",
    borderRadius: "var(--border-radius-4)",
    backgroundColor: "var(--background-accent-tofino-weakest)",
  }),
  panel: css({
    transition: "gap 0.2s ease-out",
    padding: "var(--space-4)",
    border: "1px solid var(--border-base)",
    borderRadius: "var(--border-radius-4)",
    backgroundColor: "var(--background-accent-tofino-weakest)",
  }),
};
